import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, useScroll, useTransform, useMotionValue, useSpring, useInView } from "framer-motion";
import { ArrowRight, ChevronDown, Leaf, Users, Zap, Percent, UserPlus, Rocket } from "lucide-react";

const ease = [0.2, 0.8, 0.2, 1];
const reveal = { hidden: { opacity: 0, y: 40 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, ease, delay: i * 0.12 } }) };

const Words = ({ text, className, nowrap = false }) => {
  let n = 0;
  return (
    <motion.p key={text} className={className} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.6 }}>
      {text.split("\n").map((line, li) => (
        <span key={li} className={`block ${nowrap ? "whitespace-nowrap" : ""}`}>
          {line.split(" ").map((w, i) => {
            const d = 0.3 + n++ * 0.045;
            return <motion.span key={i} className="inline-block mr-[0.28em]" variants={{ hidden: { opacity: 0, y: 20, filter: "blur(6px)" }, show: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.5, ease, delay: d } } }}>{w}</motion.span>;
          })}
        </span>
      ))}
    </motion.p>
  );
};

const Scene0 = ({ progress }) => {
  const { t } = useTranslation();
  const mx = useMotionValue(0), my = useMotionValue(0);
  const spring = { stiffness: 60, damping: 18, mass: 0.6 };
  const tx = useSpring(useTransform(mx, [-0.5, 0.5], [-70, 70]), spring);
  const ty = useSpring(useTransform(my, [-0.5, 0.5], [-45, 45]), spring);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), spring);
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), spring);
  const gx = useSpring(useTransform(mx, [-0.5, 0.5], [60, -60]), spring);
  const gy = useSpring(useTransform(my, [-0.5, 0.5], [40, -40]), spring);
  const iconY = useTransform(progress, [0, 1], [0, -120]);
  const iconScale = useTransform(progress, [0, 1], [1, 0.7]);
  const fade = useTransform(progress, [0, 0.6], [1, 0]);

  const clamp = (v) => Math.max(-0.5, Math.min(0.5, v));

  useEffect(() => {
    const onMove = (e) => { mx.set(e.clientX / window.innerWidth - 0.5); my.set(e.clientY / window.innerHeight - 0.5); };
    const onTouch = (e) => { const t = e.touches[0]; if (t) { mx.set(t.clientX / window.innerWidth - 0.5); my.set(t.clientY / window.innerHeight - 0.5); } };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onTouch, { passive: true });
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("touchmove", onTouch); };
  }, [mx, my]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.DeviceOrientationEvent || !window.matchMedia("(pointer: coarse)").matches) return;
    let base = null;
    const onTilt = (e) => {
      if (e.gamma == null || e.beta == null) return;
      if (base === null) base = { b: e.beta, g: e.gamma };
      mx.set(clamp((e.gamma - base.g) / 45));
      my.set(clamp((e.beta - base.b) / 45));
    };
    let cleanupGesture = () => {};
    if (typeof DeviceOrientationEvent.requestPermission === "function") {
      const ask = async () => {
        cleanupGesture();
        try { const r = await DeviceOrientationEvent.requestPermission(); if (r === "granted") window.addEventListener("deviceorientation", onTilt); } catch {}
      };
      DeviceOrientationEvent.requestPermission().then((r) => { if (r === "granted") { window.addEventListener("deviceorientation", onTilt); cleanupGesture(); } }).catch(() => {});
      const evs = ["touchend", "click", "scroll"];
      evs.forEach((e) => window.addEventListener(e, ask, { once: true, passive: true }));
      cleanupGesture = () => evs.forEach((e) => window.removeEventListener(e, ask));
    } else {
      window.addEventListener("deviceorientation", onTilt);
    }
    return () => { window.removeEventListener("deviceorientation", onTilt); cleanupGesture(); };
  }, [mx, my]);

  return (
    <div data-testid="intro-scene-0" className="relative flex min-h-[calc(100svh-112px)] flex-col items-center justify-center overflow-hidden px-4 pb-8 pt-4 text-center lg:min-h-[calc(100svh-132px)]">
      <motion.div style={{ opacity: fade }} className="pointer-events-none absolute inset-0">
        <motion.div style={{ x: gx, y: gy }} className="absolute left-1/2 top-1/2 h-[70vmin] w-[70vmin] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#c6ff00]/20 blur-[120px]" />
      </motion.div>
      <motion.div style={{ y: iconY, scale: iconScale }} className="relative">
        <motion.div style={{ x: tx, y: ty, rotateX: rx, rotateY: ry, transformPerspective: 900 }}>
          <motion.img src="/brand/icon.png" alt="NINJAVITY" data-testid="intro-icon" className="h-[min(52vmin,30svh)] w-[min(52vmin,30svh)] min-h-[140px] min-w-[140px] [@media(max-height:720px)]:h-[24svh] [@media(max-height:720px)]:w-[24svh] max-h-[380px] max-w-[380px] drop-shadow-[0_40px_80px_rgba(198,255,0,0.45)]" initial={{ opacity: 0, scale: 0.7, filter: "blur(12px)" }} animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }} transition={{ duration: 1.2, ease }} />
        </motion.div>
      </motion.div>
      <motion.h1 style={{ opacity: fade }} className="nv-brand-font relative z-10 mt-4 text-[13vw] [@media(max-height:720px)]:mt-2 font-black uppercase leading-none tracking-tight sm:text-6xl lg:text-[min(7rem,11svh)]" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease, delay: 0.4 }}>
        NINJA<span className="text-[#c6ff00]">VITY</span>
      </motion.h1>
      <motion.div style={{ opacity: fade }} className="relative z-10">
        <Words text={t("intro.claim")} nowrap className="mx-auto mt-6 [@media(max-height:720px)]:mt-3 font-display text-[12.5px] font-medium text-white/80 min-[420px]:text-base sm:text-xl lg:text-2xl" />
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.6, duration: 0.6, ease }}>
          <Link to="/register" data-testid="intro-enter-cta" className="nv-btn-lime mt-8 !px-10 !py-5 !text-base sm:mt-10 [@media(max-height:720px)]:!mt-5 sm:!px-12 sm:!py-6 sm:!text-lg shadow-[0_0_60px_-10px_rgba(198,255,0,0.6)] hover:shadow-[0_0_80px_-5px_rgba(198,255,0,0.7)]">
            {t("intro.enter")} <ArrowRight size={22} />
          </Link>
        </motion.div>
      </motion.div>
      <motion.div style={{ opacity: fade }} className="pointer-events-none relative z-10 mt-8 flex justify-center [@media(max-height:720px)]:mt-2">
        <motion.div data-testid="intro-scroll-hint" className="flex flex-col items-center gap-1 text-[10px] font-bold uppercase tracking-[0.3em] text-white/60" animate={{ y: [0, 8, 0] }} transition={{ duration: 1.6, repeat: Infinity }}>
          {t("intro.scroll")} <ChevronDown size={16} />
        </motion.div>
      </motion.div>
    </div>
  );
};

const StepCard = ({ n, icon: Icon, title, desc, i }) => (
  <motion.div custom={i} variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.4 }} whileHover={{ y: -8 }} data-testid={`intro-step1-${n}`} className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04] p-8 backdrop-blur-xl transition-colors duration-300 hover:border-[#c6ff00]/60 hover:bg-[#c6ff00] hover:text-black">
    <span className="font-display text-6xl font-bold text-white/10 transition-colors group-hover:text-black/15">0{n}</span>
    <div className="mt-2 grid h-12 w-12 place-items-center rounded-full bg-[#c6ff00] text-black transition-colors group-hover:bg-black group-hover:text-[#c6ff00]"><Icon size={22} /></div>
    <h3 className="mt-5 font-display text-2xl font-bold">{title}</h3>
    <p className="mt-3 text-sm leading-relaxed text-white/65 transition-colors group-hover:text-black/75">{desc}</p>
  </motion.div>
);

const Scene1 = () => {
  const { t } = useTranslation();
  const steps = t("intro.s1", { returnObjects: true });
  const icons = [Leaf, Users, Zap];
  return (
    <div data-testid="intro-scene-1" className="nv-container relative py-28">
      <motion.p variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="nv-eyebrow !text-[#c6ff00]">NINJAVITY</motion.p>
      <motion.h2 variants={reveal} custom={1} initial="hidden" whileInView="show" viewport={{ once: true }} className="mt-3 font-display text-3xl font-bold sm:text-5xl lg:text-6xl">{t("intro.s1Title")} <span className="whitespace-nowrap text-[#c6ff00]">{t("intro.s1Hl")}</span></motion.h2>
      <div className="mt-14 grid gap-5 md:grid-cols-3">
        {steps.map((s, i) => <StepCard key={i} n={i + 1} i={i} icon={icons[i]} title={s.t} desc={s.d} />)}
      </div>
      <div className="mt-28 text-center">
        <motion.p initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, amount: 0.6 }} transition={{ duration: 0.7, ease }} className="font-display text-5xl font-bold sm:text-7xl lg:text-8xl">
          {t("intro.quote1")} <motion.span className="text-[#c6ff00]" initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.5, duration: 0.6, ease }}>{t("intro.quote2")}</motion.span>
        </motion.p>
        <Words text={t("intro.quote3")} className="mx-auto mt-8 max-w-3xl font-display text-xl font-semibold text-white/70 sm:text-2xl lg:text-3xl" />
      </div>
    </div>
  );
};

const Scene2 = () => {
  const { t } = useTranslation();
  const steps = t("intro.s2", { returnObjects: true });
  const icons = [Percent, UserPlus, Rocket];
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const inViewRef = useRef(null);
  const inView = useInView(inViewRef, { once: true, amount: 0.35 });
  useEffect(() => {
    if (paused || !inView) return;
    const id = setInterval(() => setActive((a) => (a + 1) % 3), 4500);
    return () => clearInterval(id);
  }, [paused, inView]);
  const Icon = icons[active];
  return (
    <div ref={inViewRef} data-testid="intro-scene-2" className="nv-container py-28" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="grid items-center gap-14 lg:grid-cols-2">
        <div>
          <motion.p variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="nv-eyebrow !text-[#c6ff00]">NINJAVITY</motion.p>
          <motion.h2 variants={reveal} custom={1} initial="hidden" whileInView="show" viewport={{ once: true }} className="mt-3 font-display text-4xl font-bold sm:text-6xl lg:text-7xl">{t("intro.s2Title")}</motion.h2>
          <ul className="mt-10 space-y-2">
            {steps.map((s, i) => (
              <li key={i}>
                <button data-testid={`intro-step2-${i + 1}`} onClick={() => setActive(i)} className={`group flex w-full items-start gap-5 rounded-2xl p-5 text-left transition-colors duration-300 ${active === i ? "bg-white/[0.06]" : "hover:bg-white/[0.03]"}`}>
                  <span className={`font-display text-sm font-bold tabular-nums transition-colors ${active === i ? "text-[#c6ff00]" : "text-white/40"}`}>0{i + 1}</span>
                  <span className="flex-1">
                    <span className={`block font-display text-xl font-bold transition-colors ${active === i ? "text-white" : "text-white/60 group-hover:text-white"}`}>{s.t}</span>
                    <motion.span initial={false} animate={{ height: active === i ? "auto" : 0, opacity: active === i ? 1 : 0 }} transition={{ duration: 0.45, ease }} className="block overflow-hidden text-sm leading-relaxed text-white/65">
                      <span className="block pt-2">{s.d}</span>
                    </motion.span>
                    <span className="mt-3 block h-0.5 w-full overflow-hidden rounded bg-white/10">
                      {active === i && inView && <motion.span key={`${i}-${paused}`} className="block h-full bg-[#c6ff00]" initial={{ width: 0 }} animate={{ width: paused ? "100%" : "100%" }} transition={{ duration: paused ? 0.3 : 4.5, ease: "linear" }} />}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="relative">
          <div className="absolute -inset-8 rounded-[3rem] bg-[#c6ff00]/15 blur-3xl" />
          <motion.div key={active} initial={{ opacity: 0, rotateY: -20, scale: 0.95 }} animate={inView ? { opacity: 1, rotateY: 0, scale: 1 } : {}} transition={{ duration: 0.6, ease }} style={{ transformPerspective: 1000 }} className="relative overflow-hidden rounded-[2.5rem] border border-white/10 bg-gradient-to-br from-white/[0.08] to-transparent p-10 backdrop-blur-xl">
            <span className="font-display text-[9rem] font-bold leading-none text-white/[0.06]">0{active + 1}</span>
            <div className="absolute right-10 top-10 grid h-20 w-20 place-items-center rounded-full bg-[#c6ff00] text-black"><Icon size={34} /></div>
            <p className="nv-eyebrow !text-[#c6ff00]">NINJAVITY</p>
            <h3 className="mt-2 font-display text-3xl font-bold">{steps[active].t}</h3>
            <p className="mt-4 text-white/70">{steps[active].d}</p>
            <img src="/brand/icon.png" alt="" className="absolute -bottom-10 -right-6 h-40 w-40 rotate-[-12deg] opacity-30" />
          </motion.div>
        </div>
      </div>
      <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="mt-24 flex flex-col items-center text-center">
        <Link to="/register" data-testid="intro-cta" className="nv-btn-lime !px-12 !py-6 !text-lg shadow-[0_0_60px_-10px_rgba(198,255,0,0.6)] hover:shadow-[0_0_80px_-5px_rgba(198,255,0,0.7)]">
          {t("intro.cta")} <ArrowRight size={22} />
        </Link>
        <p className="mt-4 text-sm text-white/50">{t("intro.ctaSub")}</p>
        <Link to="/eshop" data-testid="intro-eshop-link" className="nv-underline mt-8 font-display text-sm font-bold tracking-[0.2em] text-white/70 hover:text-white">{t("nav.eshop")} →</Link>
      </motion.div>
    </div>
  );
};

export const IntroManifesto = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  return (
    <section data-testid="intro-manifesto" className="nv-grain relative bg-[#0a0a0a] text-white">
      <div ref={ref}><Scene0 progress={scrollYProgress} /></div>
      <div className="h-px w-full bg-gradient-to-r from-transparent via-[#c6ff00]/40 to-transparent" />
      <Scene1 />
      <div className="h-px w-full bg-gradient-to-r from-transparent via-[#c6ff00]/40 to-transparent" />
      <Scene2 />
    </section>
  );
};
